import { risk } from '@/utils/enum'

export const elStatus = (measured, target) => {
  if (measured == null || target == null || target === 0)
    return { risk: risk.NA, ecart: null }
  const ecart = (Math.abs(measured - target) / Math.abs(target)) * 100
  let value = risk.HR
  if (ecart <= 2) value = risk.LR
  else if (ecart <= 5) value = risk.AR
  return { risk: value, ecart }
}

export const airStatus = (measured, target) => {
  if (measured == null || target == null) return { risk: risk.NA, ecart: null }
  const ecart = measured - target
  const abs = Math.abs(ecart)
  let value = risk.HR
  if (abs <= 0.5) value = risk.LR
  else if (abs <= 1) value = risk.AR
  return { risk: value, ecart }
}

export const slumpStatus = (measured, target, opts = {}) => {
  if (measured == null || target == null) return { risk: risk.NA, ecart: null }
  const ecart = measured - target
  const abs = Math.abs(ecart)
  let value = risk.HR
  if (abs <= 20) value = risk.LR
  else if (abs <= 30) value = risk.AR

  const high = ecart > 20
  const elBad = opts.elRisk === risk.AR || opts.elRisk === risk.HR
  if (high && elBad) value = risk.HR
  else if (high && opts.elRisk === risk.LR && opts.superplastifiant)
    value = risk.AR

  return { risk: value, ecart }
}

export const temperatureStatus = (measured) => {
  if (measured == null) return { risk: risk.NA, ecart: null }
  let value = risk.HR
  if (measured >= 10 && measured <= 25) value = risk.LR
  else if (
    (measured > 25 && measured <= 30) ||
    (measured >= 4 && measured < 10)
  )
    value = risk.AR
  return { risk: value, ecart: null }
}

export const mvStatus = (measured, target) => {
  if (measured == null || target == null || target === 0)
    return { risk: risk.NA, ecart: null }
  const ecart = (Math.abs(measured - target) / Math.abs(target)) * 100
  let value = risk.HR
  if (ecart <= 1) value = risk.LR
  else if (ecart <= 2) value = risk.AR
  return { risk: value, ecart }
}

export const delayStatus = (minutes) => {
  if (minutes == null || !Number.isFinite(minutes))
    return { risk: risk.NA, ecart: null }
  let value = risk.HR
  if (minutes < 60) value = risk.LR
  else if (minutes <= 90) value = risk.AR
  return { risk: value, ecart: minutes }
}

const piecewiseScore = (ecart, bonMax, watchMax) => {
  if (ecart == null || !Number.isFinite(ecart)) return null
  if (ecart <= bonMax) return 100 - (ecart / bonMax) * 34
  if (ecart <= watchMax)
    return 66 - ((ecart - bonMax) / (watchMax - bonMax)) * 33
  return Math.max(0, 33 - (ecart - watchMax) * 10)
}

const riskToScore = (riskValue) => {
  switch (riskValue) {
    case risk.LR:
      return 85
    case risk.AR:
      return 50
    case risk.HR:
      return 15
    default:
      return null
  }
}

export const elScore = (measured, target) => {
  if (measured == null || target == null || target === 0) return null
  return piecewiseScore(
    (Math.abs(measured - target) / Math.abs(target)) * 100,
    2,
    5,
  )
}

export const airScore = (measured, target) => {
  if (measured == null || target == null) return null
  return piecewiseScore(Math.abs(measured - target), 0.5, 1)
}

export const slumpScore = (measured, target) => {
  if (measured == null || target == null) return null
  return piecewiseScore(Math.abs(measured - target), 20, 30)
}

export const mvScore = (measured, target) => {
  if (measured == null || target == null || target === 0) return null
  return piecewiseScore(
    (Math.abs(measured - target) / Math.abs(target)) * 100,
    1,
    2,
  )
}

export const temperatureScore = (measured) =>
  riskToScore(temperatureStatus(measured).risk)

export const delayScore = (minutes) => riskToScore(delayStatus(minutes).risk)

export const riskColor = (value) => {
  switch (value) {
    case risk.LR:
      return 'success'
    case risk.AR:
      return 'warning'
    case risk.HR:
      return 'error'
    default:
      return 'secondary'
  }
}

export const formatDelay = (minutes) => {
  if (minutes == null || !Number.isFinite(minutes)) return '—'
  return `${Math.round(minutes)} min`
}

export const formatFc = (mpa) => {
  if (mpa == null || !Number.isFinite(mpa)) return '—'
  return `${mpa.toFixed(1)} MPa`
}

export const formatValue = (value, unit = '') => {
  if (value == null || !Number.isFinite(value)) return '—'
  return unit ? `${value} ${unit}` : `${value}`
}

const normDev = (absEcart, bon, watch) => {
  if (absEcart == null || !Number.isFinite(absEcart)) return null
  if (absEcart <= bon) return (absEcart / bon) * 33
  if (absEcart <= watch) return 33 + ((absEcart - bon) / (watch - bon)) * 33
  return Math.min(100, 66 + ((absEcart - watch) / watch) * 34)
}

const dir = (m, t) => (m >= t ? 1 : -1)

export const elVariation = (m, t) =>
  m == null || !t
    ? null
    : normDev(Math.abs(((m - t) / t) * 100), 2, 5) * dir(m, t)
export const airVariation = (m, t) =>
  m == null || t == null ? null : normDev(Math.abs(m - t), 0.5, 1) * dir(m, t)
export const slumpVariation = (m, t) =>
  m == null || t == null ? null : normDev(Math.abs(m - t), 20, 30) * dir(m, t)
export const mvVariation = (m, t) =>
  m == null || !t
    ? null
    : normDev(Math.abs(((m - t) / t) * 100), 1, 2) * dir(m, t)
export const tempVariation = (m) => {
  if (m == null || !Number.isFinite(m)) return null
  if (m >= 10 && m <= 25) return 0
  if (m > 25) return Math.min(100, 33 + ((m - 25) / 5) * 33)
  return -Math.min(100, 33 + ((10 - m) / 6) * 33)
}
export const delayVariation = (m) => {
  if (m == null || !Number.isFinite(m)) return null
  if (m < 60) return (m / 60) * 33
  if (m <= 90) return 33 + ((m - 60) / 30) * 33
  return Math.min(100, 66 + (m - 90) * 1.13)
}

export const concreteVariations = (latest = {}, targets = {}) => {
  const items = [
    { id: 'el', name: 'el', value: elVariation(latest.el, targets.elCible) },
    {
      id: 'air',
      name: 'air',
      value: airVariation(latest.air, targets.airVise),
    },
    {
      id: 'slump',
      name: 'slump',
      value: slumpVariation(latest.slump, targets.affaissementCible),
    },
    {
      id: 'temperature',
      name: 'temperature',
      value: tempVariation(latest.temperature),
    },
    {
      id: 'unitWeight',
      name: 'unitWeight',
      value: mvVariation(latest.unitWeight, targets.mvCible),
    },
    { id: 'delay', name: 'delay', value: delayVariation(latest.delay) },
  ]
  return items.filter((i) => i.value != null)
}

const BANDS = {
  el: {
    dev: (m, t) => (t.elCible ? ((m - t.elCible) / t.elCible) * 100 : null),
    lr: 2,
    ar: 5,
    hr: 8,
  },
  air: { dev: (m, t) => m - t.airVise, lr: 0.5, ar: 1, hr: 1.5 },
  slump: { dev: (m, t) => m - t.affaissementCible, lr: 20, ar: 30, hr: 40 },
  unitWeight: {
    dev: (m, t) => (t.mvCible ? ((m - t.mvCible) / t.mvCible) * 100 : null),
    lr: 1,
    ar: 2,
    hr: 3,
  },
  temperature: { dev: (m) => m - 17.5, lr: 7.5, ar: 13, hr: 18 },
  delay: { dev: (m) => m, lr: 60, ar: 90, hr: 120 },
}

export const paramTimeline = (param, samples = [], targets = {}) => {
  const cfg = BANDS[param] || BANDS.air
  const deviations = samples
    .map((s) => cfg.dev(s[param], targets))
    .filter((v) => v != null && Number.isFinite(v))
  return {
    deviations: deviations.length ? deviations : [0],
    upperLR: cfg.lr,
    upperAR: cfg.ar,
    upperHR: cfg.hr,
    lowerLR: -cfg.lr,
    lowerAR: -cfg.ar,
    lowerHR: -cfg.hr,
  }
}
